import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Loader2, FileText, ShieldCheck, Ruler, Coins } from "lucide-react";
import SealBadge from "@/components/SealBadge";

/**
 * GiftPreview — full-screen luxury "unboxing" preview of the exact Lob postcard PDF
 * (front artwork + back message + Safe-Zone guides) before authorizing the mailing.
 *
 * Logic Audit — the Full Review step discloses BOTH the exact final print dimensions and
 * the Maison Service Fee (1 Mailing Credit vs the flat $9.99 Boutique rate) with zero
 * surprises before the sender ever taps Send.
 */
const GiftPreview = ({ cardId, open, onClose, onSend }) => {
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState("");
  const [spec, setSpec] = useState("");
  const [dimensions, setDimensions] = useState(null);
  const [feeInfo, setFeeInfo] = useState(null);
  // Preview Integrity — the spinner must stay active until the PDF iframe has ACTUALLY
  // painted (onLoad) AND document.fonts.ready has resolved, eliminating the "incomplete"
  // flash where the frame briefly shows before the PDF plugin finishes rendering.
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  // The Maison Seal Animation — a brief, rewarding stamp cue the instant the sender confirms
  // this Full Review preview, before the actual send() call fires.
  const [stamping, setStamping] = useState(false);

  useEffect(() => {
    if (!open || !cardId) return;
    setLoading(true);
    setError("");
    setPdfUrl(null);
    setStamping(false);
    setIframeLoaded(false);
    setFontsReady(false);
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(() => setFontsReady(true)).catch(() => setFontsReady(true));
    } else {
      setFontsReady(true);
    }
    api
      .get(`/cards/${cardId}/preview-pdf`)
      .then((res) => {
        setSpec(res.data.spec || "");
        setDimensions(res.data.dimensions || null);
        setFeeInfo(res.data.fee_info || null);
        // build a blob URL from base64 for reliable in-browser PDF rendering
        const byteChars = atob(res.data.pdf_base64);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
        const blob = new Blob([bytes], { type: "application/pdf" });
        setPdfUrl(URL.createObjectURL(blob));
      })
      .catch((e) => setError(e?.response?.data?.detail || "Could not render preview."))
      .finally(() => setLoading(false));
  }, [open, cardId]);

  // Safety net — some browsers/headless environments don't reliably fire `load` for a PDF
  // rendered inside an <iframe> via a blob: URL (the built-in PDF viewer takes over
  // rendering). Never let the spinner hang forever: force-reveal after a short grace period
  // if onLoad hasn't fired by then, while still preferring the real onLoad signal when it
  // does arrive sooner (the whole point of the Preview Integrity fix).
  useEffect(() => {
    if (!pdfUrl) return;
    const t = setTimeout(() => setIframeLoaded(true), 2500);
    return () => clearTimeout(t);
  }, [pdfUrl]);

  if (!open) return null;

  // Still "loading" from the sender's perspective until the fetch resolved AND the iframe
  // has actually painted the PDF AND the document's fonts have settled — no premature reveal.
  const stillRendering = loading || (!!pdfUrl && (!iframeLoaded || !fontsReady));

  // Confirm Preview — fires the Maison Seal Animation first (a small sensory reward), then
  // hands off to the real send() a beat later so the stamp is actually seen.
  const confirmAndSend = () => {
    setStamping(true);
    setTimeout(() => { onSend && onSend(); }, 650);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[hsl(38_30%_12%_/_0.55)] backdrop-blur-md flex flex-col" data-testid="gift-preview">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2 text-white">
          <FileText className="w-5 h-5" />
          <span className="font-serif text-xl">Full Review</span>
          {spec && <span className="hidden sm:inline text-xs text-white/70 ml-2">{spec}</span>}
        </div>
        <button data-testid="gift-preview-close" onClick={onClose}
          className="p-2 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-4 relative">
        {error ? (
          <div className="text-red-200">{error}</div>
        ) : (
          <div className="w-full max-w-4xl h-full bg-white rounded-3xl overflow-hidden shadow-2xl relative">
            {pdfUrl && (
              <iframe
                title="Gift Preview PDF"
                src={pdfUrl}
                onLoad={() => setIframeLoaded(true)}
                className="w-full h-full transition-opacity duration-300"
                style={{ opacity: stillRendering ? 0 : 1 }}
                data-testid="gift-preview-frame"
              />
            )}
            {/* Preview Integrity overlay — covers the frame until it has TRULY finished
                painting, eliminating the "incomplete" flash testers reported. */}
            {stillRendering && (
              <div className="absolute inset-0 flex items-center justify-center bg-white" data-testid="gift-preview-loading-overlay">
                <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Rendering your card…</div>
              </div>
            )}
            {/* The Maison Seal Animation — stamps down over the preview the instant Confirm
                Preview is clicked, a rewarding beat before the real send fires. */}
            {stamping && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20" data-testid="maison-seal-animation">
                <div className="seal-stamp-down"><SealBadge tier="gold" size={140} /></div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Logic Audit — exact final dimensions + the Maison Service Fee, clearly disclosed */}
      {!stillRendering && !error && (dimensions || feeInfo) && (
        <div className="px-6 pb-2 flex flex-wrap items-center justify-center gap-3 text-xs text-white/80" data-testid="full-review-disclosure">
          {dimensions && (
            <span className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5" data-testid="final-dimensions">
              <Ruler className="w-3.5 h-3.5" /> Final size: {dimensions.width_in}&quot; × {dimensions.height_in}&quot; (+{dimensions.bleed_in}&quot; bleed)
            </span>
          )}
          {feeInfo && (
            <span className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5" data-testid="maison-service-fee">
              <Coins className="w-3.5 h-3.5 gold-text" />
              {feeInfo.will_use_credit
                ? "Maison Service Fee: 1 Mailing Credit — no charge today"
                : `Maison Service Fee: $${Number(feeInfo.service_fee).toFixed(2)} — charged when you send`}
            </span>
          )}
        </div>
      )}

      <div className="px-6 py-4 flex items-center justify-center gap-3">
        <div className="text-white/70 text-xs flex items-center gap-1.5 mr-auto">
          <ShieldCheck className="w-4 h-4" /> Green dashes mark the Lob Safe Zone — keep key content inside.
        </div>
        <button onClick={onClose} className="px-5 py-2.5 rounded-2xl bg-white/15 text-white hover:bg-white/25">Close</button>
        {onSend && (
          <button data-testid="gift-preview-send" onClick={confirmAndSend} disabled={stamping || stillRendering}
            className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-secondary to-primary text-white hover:opacity-90 disabled:opacity-70">
            {stamping ? "Sealing…" : "Looks perfect — Send"}
          </button>
        )}
      </div>
    </div>
  );
};

export default GiftPreview;
